import { isTruthy } from '@ardoq/common-helpers';
import { Column, ExpandableRowData } from '@ardoq/table-legacy';
import { ReferenceAttributesTable } from './ReferenceAttributesTable';
import { ReferenceTableRow, ReferenceAttributeRow } from '../types';
import { s8 } from '@ardoq/design-tokens';
import { SmallGhostButton } from '@ardoq/button';
import { AnimatedChevron } from '@ardoq/icons';
import { TextOverflow } from '@ardoq/popovers';
import { ResolvedComponentIcon } from './ResolvedComponentIcon';
import { APIComponentAttributes, ArdoqId, SortOrder } from '@ardoq/api-types';
import { Paragraph, text2 } from '@ardoq/typography';
import { Link } from '@ardoq/link';
import { EnhancedScopeData } from '@ardoq/data-model';
import styled from 'styled-components';
import { SortByFn, SortById } from '@ardoq/pagination';
import { FlexBox } from '@ardoq/layout';

type ComponentProps = {
  component: APIComponentAttributes;
  enhancedScopeData: EnhancedScopeData;
  onClick?: (componentId: string) => void;
};

const ComponentNameAndIcon = ({
  component,
  enhancedScopeData,
  onClick,
}: ComponentProps) => {
  return (
    <FlexBox gap="xsmall" align="center">
      <ResolvedComponentIcon
        component={component}
        scopeData={enhancedScopeData}
      />
      {onClick ? (
        <Link onClick={() => onClick(component._id)} hideIcon underlineOnHover>
          <Paragraph color="textDefault" variant="text2Bold">
            {component.name}
          </Paragraph>
        </Link>
      ) : (
        <Paragraph color="textDefault" variant="text2Bold">
          {component.name}
        </Paragraph>
      )}
    </FlexBox>
  );
};

export const isReferenceAttributeRow = (
  row: ReferenceTableRow
): row is ReferenceAttributeRow =>
  row && (row as ReferenceAttributeRow).isExpandableRow === true;

export const SourceColumnRenderer = ({
  rowData,
  scopeData,
  onComponentClick,
  contextComponentId,
}: {
  rowData: ExpandableRowData<ReferenceTableRow>;
  scopeData: EnhancedScopeData;
  onComponentClick?: (componentId: ArdoqId) => void;
  contextComponentId: string;
}) => {
  if (isReferenceAttributeRow(rowData))
    return (
      <ReferenceAttributesTable
        reference={rowData.reference}
        scopeData={scopeData}
        detailEntries={rowData.detailEntries}
        referenceTypeName={rowData.referenceTypeName}
      />
    );
  // sadly parent references are the wrong way around
  const reference = rowData.children[0].reference;
  const sourceComponent = reference.virtual
    ? scopeData.componentsById[reference.target]
    : scopeData.componentsById[reference.source];

  if (!sourceComponent) {
    return null;
  }

  return (
    <ComponentNameAndIcon
      onClick={
        sourceComponent._id !== contextComponentId
          ? onComponentClick
          : undefined
      }
      component={sourceComponent}
      enhancedScopeData={scopeData}
    />
  );
};

const getSourceColumn = (
  sortById: SortById,
  sortOrder: SortOrder,
  sortBy: SortByFn,
  scopeData: EnhancedScopeData,
  contextComponentId: string,
  onComponentClick?: (componentId: ArdoqId) => void,
  title: string = 'Source'
): Column<ExpandableRowData<ReferenceTableRow>> => {
  return {
    title,
    dataIndex: 'sourceName',
    headerStyle: { paddingBottom: s8, width: '30%' },
    sortOrder: sortById === 'sourceName' && sortOrder,
    onHeaderClick: () => sortBy('sourceName'),
    getColSpan: (_v, rowData) => (!isReferenceAttributeRow(rowData) ? 0 : 4),
    valueRender: (_v, rowData) => (
      <SourceColumnRenderer
        rowData={rowData}
        scopeData={scopeData}
        onComponentClick={onComponentClick}
        contextComponentId={contextComponentId}
      />
    ),
  };
};

const StyledTextOverflow = styled(TextOverflow)`
  ${text2};
  max-width: 200px;
  font-style: italic;
  width: 100%;
`;

export const TypeColumnRenderer = ({
  rowData,
}: {
  rowData: ExpandableRowData<ReferenceTableRow>;
}) => {
  if (isReferenceAttributeRow(rowData)) return null;
  return <StyledTextOverflow>{rowData.referenceTypeName}</StyledTextOverflow>;
};

const getTypeColumn = (
  sortById: SortById,
  sortOrder: SortOrder,
  sortBy: SortByFn,
  title: string = 'Type'
): Column<ExpandableRowData<ReferenceTableRow>> => ({
  title,
  dataIndex: 'referenceTypeName',
  headerStyle: { paddingBottom: s8, width: '30%' },
  sortOrder: sortById === 'referenceTypeName' && sortOrder,
  onHeaderClick: () => sortBy('referenceTypeName'),
  valueRender: (_any: any, rowData) => <TypeColumnRenderer rowData={rowData} />,
});

export const TargetColumnRenderer = ({
  rowData,
  scopeData,
  onComponentClick,
  contextComponentId,
}: {
  rowData: ExpandableRowData<ReferenceTableRow>;
  scopeData: EnhancedScopeData;
  onComponentClick?: (componentId: ArdoqId) => void;
  contextComponentId: string;
}) => {
  if (isReferenceAttributeRow(rowData)) return null;
  // sadly parent references are the wrong way around
  const reference = rowData.children[0].reference;
  const targetComponent = reference.virtual
    ? scopeData.componentsById[reference.source]
    : scopeData.componentsById[reference.target];

  if (!targetComponent) {
    return null;
  }

  return (
    <ComponentNameAndIcon
      onClick={
        targetComponent._id !== contextComponentId
          ? onComponentClick
          : undefined
      }
      component={targetComponent}
      enhancedScopeData={scopeData}
    />
  );
};

const getTargetColumn = (
  sortById: SortById,
  sortOrder: SortOrder,
  sortBy: SortByFn,
  scopeData: EnhancedScopeData,
  contextComponentId: string,
  onComponentClick?: (componentId: ArdoqId) => void,
  title: string = 'Target'
): Column<ExpandableRowData<ReferenceTableRow>> => ({
  title,
  dataIndex: 'targetName',
  headerStyle: { paddingBottom: s8, width: '30%' },
  sortOrder: sortById === 'targetName' && sortOrder,
  onHeaderClick: () => sortBy('targetName'),
  valueRender: (_any, rowData) => (
    <TargetColumnRenderer
      rowData={rowData}
      scopeData={scopeData}
      onComponentClick={onComponentClick}
      contextComponentId={contextComponentId}
    />
  ),
});

export const ExpanderColumnRenderer = ({
  rowData,
  expandedRowIds,
  expandRow,
}: {
  rowData: ExpandableRowData<ReferenceTableRow>;
  expandedRowIds: string[];
  expandRow: (path: string) => void;
}) => {
  if (isReferenceAttributeRow(rowData) || rowData.children[0].reference.virtual)
    return null;
  const { path } = rowData.meta;
  const isExpanded = expandedRowIds.includes(path);
  return (
    <SmallGhostButton onClick={() => expandRow(path)}>
      <AnimatedChevron $isUp={isExpanded} />
    </SmallGhostButton>
  );
};

const getExpanderColumnConfig = (
  expandedRowIds: string[],
  expandRow: (path: string) => void
) => ({
  headerStyle: { paddingBottom: s8, width: '5%' },
  valueRender: (_any: any, rowData: ExpandableRowData<ReferenceTableRow>) => (
    <ExpanderColumnRenderer
      rowData={rowData}
      expandedRowIds={expandedRowIds}
      expandRow={expandRow}
    />
  ),
});

export type ReferenceColumnLabels = {
  source?: string;
  type?: string;
  target?: string;
};

export const getReferenceColumns = (
  expandedRowIds: string[],
  sortById: SortById,
  sortOrder: SortOrder,
  expandRow: (path: string) => void,
  sortBy: SortByFn,
  scopeData: EnhancedScopeData,
  contextComponentId: string,
  onComponentClick?: (componentId: ArdoqId) => void,
  labels: ReferenceColumnLabels = {}
) =>
  [
    getSourceColumn(
      sortById,
      sortOrder,
      sortBy,
      scopeData,
      contextComponentId,
      onComponentClick,
      labels.source
    ),
    getTypeColumn(sortById, sortOrder, sortBy, labels.type),
    getTargetColumn(
      sortById,
      sortOrder,
      sortBy,
      scopeData,
      contextComponentId,
      onComponentClick,
      labels.target
    ),
    getExpanderColumnConfig(expandedRowIds, expandRow),
  ].filter(isTruthy);
